import { useEffect, useState } from 'react';
import { Plus, Inbox, X } from 'lucide-react';
import { useAgreementStore } from '@/modules/agreements/store/agreementStore';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { usePleas } from '../hooks/usePleas';
import { usePleaForm } from '../hooks/usePleaForm';
import { usePleaDetail } from '../hooks/usePleaDetail';
import { PleaCard } from '../components/PleaCard';
import { PleaFormSheet } from '../components/PleaFormSheet';
import { PleaDetailDialog } from '../components/PleaDetailDialog';
import { DeleteConfirmSheet } from '../components/DeleteConfirmSheet';
import { StatusFilterChips } from '../components/StatusFilterChips';
import type { Plea } from '../types';

export default function PleasPage() {
  const { activeAgreement } = useAgreementStore();
  const { pleas, loading, error, setError, refetch } = usePleas(activeAgreement?.uuid);
  const [notice, setNotice] = useState<string | null>(null);
  const form = usePleaForm({
    agreementUuid: activeAgreement?.uuid,
    onSuccess: () => refetch({ silent: true }),
    onError: setError,
    onPleaDecided: (message) => {
      setNotice(message);
      refetch({ silent: true });
    },
  });
  const detail = usePleaDetail({ agreementUuid: activeAgreement?.uuid });

  const [statusFilter, setStatusFilter] = useState('all');

  const handleEditFromDetail = (plea: Plea) => {
    detail.closeView();
    form.openEdit(plea);
  };

  useEffect(() => {
    document.title = 'Requerimentos';
  }, []);

  if (!activeAgreement) {
    return (
      <div className="flex flex-col items-center px-6 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
          <Inbox className="h-8 w-8 text-slate-300" aria-hidden="true" />
        </div>
        <p className="text-sm text-slate-500">Selecione um acordo para ver os requerimentos.</p>
      </div>
    );
  }

  const filteredPleas =
    statusFilter === 'all'
      ? pleas
      : pleas.filter((p) => (p.status ?? 'pending').toLowerCase() === statusFilter);

  return (
    <div className="flex flex-col">
      <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-6 pb-4 pt-6">
        <h1 className="text-xl font-bold text-slate-900">Requerimentos</h1>
        <button
          onClick={form.openAdd}
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white shadow-sm hover:bg-slate-800 transition-colors"
          aria-label="Novo pedido"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div className="px-4 pt-4">
        {notice && (
          <div
            role="status"
            className="mb-3 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} aria-label="Fechar aviso" className="shrink-0 text-amber-700 hover:text-amber-900">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}

        {!loading && pleas.length > 0 && (
          <StatusFilterChips activeFilter={statusFilter} onFilterChange={setStatusFilter} pleas={pleas} />
        )}

        {loading ? (
          <div className="space-y-3" role="status" aria-label="Carregando pedidos">
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
            <Skeleton className="h-24 w-full rounded-2xl" />
          </div>
        ) : error ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
            <Button variant="outline" size="sm" className="ml-3" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          </div>
        ) : filteredPleas.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
              <Inbox className="h-8 w-8 text-slate-300" aria-hidden="true" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-slate-900">Nenhum requerimento</h3>
            <p className="mb-6 text-xs text-slate-500">
              {statusFilter === 'all'
                ? 'Você não tem solicitações abertas no momento.'
                : 'Nenhum pedido com este status.'}
            </p>
            {statusFilter === 'all' && (
              <Button variant="outline" onClick={form.openAdd}>Criar Solicitação</Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredPleas.map((p, idx) => (
              <PleaCard
                key={p.self || idx}
                plea={p}
                onEdit={form.openEdit}
                onDelete={form.openDelete}
                onView={detail.openView}
                onViewPeekEnd={detail.closeView}
              />
            ))}
          </div>
        )}
      </div>

      <PleaFormSheet
        open={form.addOpen}
        onClose={form.closeAdd}
        title="Novo pedido"
        form={form.form}
        onChange={form.setForm}
        onSubmit={form.handleAdd}
        submitting={form.submitting}
      />

      <PleaFormSheet
        open={form.editOpen}
        onClose={form.closeEdit}
        title="Editar pedido"
        form={form.editForm}
        onChange={form.setEditForm}
        onSubmit={form.handleEditSubmit}
        submitting={form.submitting}
        attachments={form.attachments}
        attachmentsLoading={form.attachmentsLoading}
        attachmentsError={form.attachmentsError}
      />

      <PleaDetailDialog
        plea={detail.selectedPlea}
        open={detail.viewOpen}
        onOpenChange={(open) => { if (!open) detail.closeView(); }}
        onEdit={handleEditFromDetail}
        attachments={detail.attachments}
        attachmentsLoading={detail.attachmentsLoading}
        attachmentsError={detail.attachmentsError}
      />

      <DeleteConfirmSheet
        open={form.deleteOpen}
        onClose={form.closeDelete}
        onConfirm={form.handleDelete}
        submitting={form.submitting}
      />
    </div>
  );
}
